import React, { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import api from "@/lib/api";
import { cardCls, EmptyState } from "@/components/admin/ui";

const RANGES = [{ label: "12 months", value: 12 }, { label: "3 years", value: 36 }];

const fmtMonth = (mk) => {
  const [y, m] = mk.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};
const gbp = (n) => "£" + Number(n || 0).toLocaleString("en-GB");

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-bd-charcoal border border-bd-gold/30 px-3 py-2 text-xs">
      <p className="text-bd-ivory mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name}: {gbp(p.value)}</p>
      ))}
    </div>
  );
}

export default function ProfitTrendsCard() {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/finance/trends?months=${months}`).then(({ data }) => setData(data)).catch(() => setData({ series: [], connected: false }));
  }, [months]);

  const series = (data?.series || []).map((s) => ({ ...s, label: fmtMonth(s.month) }));
  const hasData = series.some((s) => s.revenue || s.expenses);

  return (
    <div data-testid="profit-trends" className={`${cardCls} p-6 mb-6`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
        <div className="flex items-center gap-3">
          <TrendingUp size={20} className="text-bd-gold" />
          <h2 className="font-serif text-xl text-bd-ivory">Profit trends</h2>
        </div>
        <div className="flex items-center gap-1 border border-white/10 p-1">
          {RANGES.map((r) => (
            <button key={r.value} data-testid={`trends-range-${r.value}`} onClick={() => setMonths(r.value)}
              className={`px-3 py-1.5 text-xs tracking-wider uppercase transition-colors ${months === r.value ? "bg-bd-gold text-bd-black" : "text-bd-ivory/50 hover:text-bd-gold"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-bd-ivory/40 mb-5">
        Month-over-month group profit (revenue − expenses).{" "}
        {data && !data.connected && <span className="text-bd-gold/80">Revenue appears once Stripe is connected — expense history is shown from your ledger.</span>}
      </p>

      {!data ? (
        <EmptyState>Loading…</EmptyState>
      ) : !hasData ? (
        <EmptyState>No finance history yet. Record expenses (and connect Stripe) to build the trend.</EmptyState>
      ) : (
        <div style={{ width: "100%", height: 320 }} data-testid="trends-chart">
          <ResponsiveContainer>
            <ComposedChart data={series} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "rgba(249,248,246,0.5)", fontSize: 11 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fill: "rgba(249,248,246,0.5)", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => "£" + (Math.abs(v) >= 1000 ? (v / 1000).toFixed(0) + "k" : v)} width={48} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(184,156,101,0.08)" }} />
              <Legend wrapperStyle={{ fontSize: 12, color: "rgba(249,248,246,0.6)" }} />
              <Bar dataKey="revenue" name="Revenue" fill="rgba(184,156,101,0.55)" barSize={14} />
              <Bar dataKey="expenses" name="Expenses" fill="rgba(248,113,113,0.5)" barSize={14} />
              <Line type="monotone" dataKey="profit" name="Profit" stroke="#B89C65" strokeWidth={2} dot={{ r: 2.5, fill: "#B89C65" }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
